import Dexie from 'dexie';
import {
  db,
  SYNC_STATUS,
  LOCAL_WORKSPACE_TENANT_ID,
  LOCAL_USER_ID
} from '../database';

/**
 * OutboxManager — the write-side of the offline sync engine.
 *
 * Every local mutation (create / update / delete) MUST have a matching
 * outbox delta or the change will never reach the cloud. Services wrap the
 * record write AND `enqueue()` in ONE Dexie transaction
 * (`db.transaction('rw', db.forms, db.syncQueue, ...)`) so a crash can
 * never produce a record without its replication delta. Dexie supports the
 * nested transaction because the inner scope (`syncQueue`) is a subset of
 * the outer scope.
 *
 * Idempotency & coalescing rules:
 *  - Outbox primary key is a caller-supplied uuid (`deltaId`). Re-enqueueing
 *    the same deltaId (crash replay, double-tap save) is a no-op.
 *  - A pending UPDATE for the same record REPLACES an older pending
 *    CREATE/UPDATE for that record: the newest full payload subsumes the
 *    older one, cutting queue churn during typing storms (auto-save).
 *  - DELETE always wins over any queued CREATE/UPDATE for that record.
 *  - A queued CREATE followed by DELETE collapses to NOTHING when no
 *    UPDATE delta ever existed for the record (it was never pushed, so it
 *    doesn't exist server-side). If an UPDATE exists, the CREATE has
 *    already been acknowledged by the server and the DELETE must be sent.
 */

export const OUTBOX_ACTIONS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE'
};

export const OUTBOX_ITEM_STATUS = {
  PENDING: 'pending',
  IN_FLIGHT: 'in_flight',
  DONE: 'done',
  FAILED: 'failed' // terminal: exceeded maxAttempts
};

// Collections eligible for sync. Anything else enqueued is rejected —
 // prevents unbounded queue growth from accidental non-synced table writes.
const SYNCABLE_COLLECTIONS = new Set([
  'forms',
  'customers',
  'properties',
  'templates'
]);

export class OutboxManager {
  /**
   * @param {Object} [options]
   * @param {number} [options.maxAttempts] retries before an item goes terminal-FAILED
   */
  constructor({ maxAttempts = 8 } = {}) {
    this.maxAttempts = maxAttempts;
  }

  /** Resolve the active tenant/user stamp for new records. */
  _resolveIdentity(identity = {}) {
    return {
      tenantId: identity.tenantId || LOCAL_WORKSPACE_TENANT_ID,
      userId: identity.userId || LOCAL_USER_ID
    };
  }

  /**
   * Enqueue a delta. Safe to call repeatedly with the same `deltaId`.
   *
   * @param {Object} delta
   * @param {string} delta.deltaId        client uuid (idempotency key)
   * @param {'forms'|'customers'|'properties'|'templates'} delta.collection
   * @param {string|number} delta.recordId local record key (instanceId or numeric id)
   * @param {'CREATE'|'UPDATE'|'DELETE'} delta.action
   * @param {Object} [delta.payload]      full record payload for CREATE/UPDATE
   * @param {{tenantId?:string, userId?:string}} [identity]
   */
  async enqueue({
    deltaId,
    collection,
    recordId,
    action,
    payload = null,
    identity = {}
  }) {
    if (!SYNCABLE_COLLECTIONS.has(collection)) {
      throw new Error(`OutboxManager: collection "${collection}" is not syncable`);
    }
    if (!OUTBOX_ACTIONS[action]) {
      throw new Error(`OutboxManager: invalid action "${action}"`);
    }
    if (!deltaId) throw new Error('OutboxManager: deltaId (uuid) is required');
    if (recordId === undefined || recordId === null || recordId === '') {
      throw new Error('OutboxManager: recordId is required');
    }
    if (action !== OUTBOX_ACTIONS.DELETE && !payload) {
      throw new Error(`OutboxManager: ${action} requires a payload`);
    }

    const { tenantId } = this._resolveIdentity(identity);
    const recordKey = String(recordId);

    await db.transaction('rw', db.syncQueue, async () => {
      // Coalescing pass: collapse same-record pending deltas per the matrix
      // in the module docblock. Scans the tenant's queue segment; queues
      // stay small (< few hundred) precisely because of this coalescing.
      const existing = await db.syncQueue
        .where('[tenantId+createdAt]')
        .between([tenantId, Dexie.minKey], [tenantId, Dexie.maxKey])
        .toArray();

      const sameRecord = existing.filter(
        (item) =>
          item.status === OUTBOX_ITEM_STATUS.PENDING &&
          item.collection === collection &&
          item.recordId === recordKey
      );

      // Rule 1 — incoming DELETE:
      //   * removes every pending CREATE/UPDATE for the record;
      //   * collapses to NOTHING when no UPDATE delta ever existed (the
      //     CREATE was never acknowledged server-side, so there is nothing
      //     to delete remotely);
      //   * survives as a standalone DELETE when an UPDATE existed, because
      //     the server copy is real and must be removed.
      if (action === OUTBOX_ACTIONS.DELETE) {
        const sawUpdate = sameRecord.some(
          (item) => item.action === OUTBOX_ACTIONS.UPDATE
        );
        await db.syncQueue.bulkDelete(sameRecord.map((item) => item.id));
        if (!sawUpdate) {
          return; // never replicated remotely — delta fully collapsed
        }
        // fall through to enqueue the standalone DELETE below.
      } else {
        // Rule 2 — incoming CREATE/UPDATE refreshes an existing pending
        // delta instead of stacking a second one. The newest full payload
        // subsumes the older one (auto-save typing storms collapse into a
        // single delta).
        for (const item of sameRecord) {
          if (
            item.action === OUTBOX_ACTIONS.CREATE ||
            item.action === OUTBOX_ACTIONS.UPDATE
          ) {
            await db.syncQueue.put({
              ...item,
              action: item.action === OUTBOX_ACTIONS.CREATE
                ? OUTBOX_ACTIONS.CREATE // never-pushed stays CREATE
                : OUTBOX_ACTIONS.UPDATE,
              payload,
              updatedAt: Date.now()
            });
            return;
          }
        }
      }

      await db.syncQueue.put({
        id: deltaId,
        tenantId,
        userId: identity.userId || LOCAL_USER_ID,
        collection,
        recordId: recordKey,
        action,
        payload,
        status: OUTBOX_ITEM_STATUS.PENDING,
        attempts: 0,
        nextAttemptAt: 0, // due immediately
        createdAt: Date.now(),
        updatedAt: Date.now(),
        lastError: null
      });
    });

    db._notifyOutbox();
  }

  /**
   * Claim the next batch of due items in strict FIFO order (oldest first).
   * Items are marked IN_FLIGHT inside the claim transaction so two
   * concurrent workers (tab + SW) can't double-push the same delta.
   *
   * @param {{limit?: number, now?: number}} [options]
   * @returns {Promise<Array>} claimed deltas
   */
  async claimBatch({ limit = 25, now = Date.now() } = {}) {
    let claimed = [];
    await db.transaction('rw', db.syncQueue, async () => {
      const due = await db.syncQueue
        .where('[status+nextAttemptAt]')
        .between(
          [OUTBOX_ITEM_STATUS.PENDING, Dexie.minKey],
          [OUTBOX_ITEM_STATUS.PENDING, now]
        )
        .sortBy('createdAt');

      claimed = due.slice(0, limit).map((item) => ({
        ...item,
        status: OUTBOX_ITEM_STATUS.IN_FLIGHT,
        attempts: item.attempts + 1,
        lastAttemptAt: now
      }));

      if (claimed.length > 0) {
        await db.syncQueue.bulkPut(claimed);
      }
    });
    return claimed;
  }

  /** Mark a successfully pushed delta as done and drop its payload. */
  async markDone(deltaId) {
    await db.syncQueue.delete(deltaId);
    db._notifyOutbox();
  }

  /**
   * Schedule retry with exponential backoff + jitter, or fail terminally.
   *
   * Re-reads the stored item to use the AUTHORITATIVE attempts count —
   * callers may hold stale snapshots (e.g. claimed concurrently by another
   * worker between their read and this call).
   *
   * @returns {'retry_scheduled'|'failed'} outcome
   */
  async markFailed(item, error) {
    const message =
      (error && (error.message || String(error))) || 'unknown sync error';

    // Authoritative state beats the caller's possibly-stale copy.
    const current = (await db.syncQueue.get(item.id)) ?? item;
    const attempts = current.attempts ?? item.attempts ?? 0;

    if (attempts >= this.maxAttempts) {
      await db.syncQueue.update(current.id, {
        status: OUTBOX_ITEM_STATUS.FAILED,
        lastError: message,
        failedAt: Date.now()
      });
      // Surface the failure on the source record so UI can flag it.
      await this._flagRecordError(current);
      db._notifyOutbox();
      return 'failed';
    }

    // Exponential backoff capped at 5 minutes, ±20% jitter to avoid
    // thundering-herd reconnects across a fleet of field devices.
    const base = Math.min(30_000 * 2 ** (attempts - 1), 300_000);
    const jitter = base * (Math.random() * 0.4 - 0.2);
    const nextAttemptAt = Date.now() + Math.max(base + jitter, 1_000);

    await db.syncQueue.update(current.id, {
      status: OUTBOX_ITEM_STATUS.PENDING,
      lastError: message,
      nextAttemptAt
    });
    db._notifyOutbox();
    return 'retry_scheduled';
  }

  /** Release IN_FLIGHT items back to pending without counting an attempt —
   * used when the worker shuts down mid-batch (page hide / tab close). */
  async releaseInFlight() {
    const inFlight = await db.syncQueue
      .where('status')
      .equals(OUTBOX_ITEM_STATUS.IN_FLIGHT)
      .toArray();

    if (inFlight.length > 0) {
      await db.syncQueue.bulkPut(
        inFlight.map((item) => ({
          ...item,
          status: OUTBOX_ITEM_STATUS.PENDING
        }))
      );
      db._notifyOutbox();
    }
    return inFlight.length;
  }

  /** Remove settled items entirely (housekeeping after user review). */
  async purgeFailed() {
    await db.syncQueue
      .where('status')
      .equals(OUTBOX_ITEM_STATUS.FAILED)
      .delete();
    db._notifyOutbox();
  }

  /**
   * Stamp the originating record so UI can show a sync-error badge.
   */
  async _flagRecordError(item) {
    try {
      const table = db.table(item.collection);
      // forms/customers/properties are keyed by ++id; templates too.
      const numericId = Number(item.recordId);
      if (!Number.isFinite(numericId)) return;
      await table.update(numericId, { syncStatus: SYNC_STATUS.ERROR });
    } catch {
      /* best-effort surface only */
    }
  }

  /** Live counts for the Sync Status Pill. */
  async stats() {
    const [pending, failed, inflight] = await Promise.all([
      db.syncQueue.where('status').equals(OUTBOX_ITEM_STATUS.PENDING).count(),
      db.syncQueue.where('status').equals(OUTBOX_ITEM_STATUS.FAILED).count(),
      db.syncQueue.where('status').equals(OUTBOX_ITEM_STATUS.IN_FLIGHT).count()
    ]);
    return { pending, failed, inflight };
  }
}

// Singleton used by services/hooks. Tests construct fresh instances.
export const outboxManager = new OutboxManager();
